import { useCallback, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Flex,
  FullscreenTakeover,
  Grid,
  QueryLoader,
  Text,
  toast,
} from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { useApplyProposedChangesSelectionMutation } from "~/graphql/supergraphHooks";
import { getInvestingProfileName } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { ExitWarningModal } from "~/pages/Compliance/Common/ExitWarningModal";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import { buildMutationPayload } from "./buildMutationPayload";
import { RETURN_TO_REVIEW } from "./constants";
import { getColumnDefs, gridOptions } from "./gridConfig";
import { SelectionProvider, useSelection } from "./SelectionContext";
import * as styles from "./styles_vanilla.css";
import { useComparisonRows } from "./useComparisonRows";
import type { ComparisonRow } from "./types";

const useLatestVersionIdFromQuery = (): number | null => {
  const location = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("latestVersionId");
    if (!raw) return null;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }, [location.search]);
};

export const ProposedChangesReview = () => (
  <SelectionProvider>
    <ProposedChangesReviewContent />
  </SelectionProvider>
);

const ProposedChangesReviewContent = () => {
  const history = useHistory();
  const { sourceProfile, targetProfile } = useParams<{
    sourceProfile: string;
    targetProfile: string;
  }>();
  const sourceProfileId = sourceProfile ? parseInt(sourceProfile, 10) : 0;
  const targetProfileId = targetProfile ? parseInt(targetProfile, 10) : 0;
  const latestVersionId = useLatestVersionIdFromQuery();

  const { caseId, caseType, programId, caseBasePath, readonly, fatcaCrsEnabled } =
    useCurrentCaseContext();
  const { getSelection, setSelection, hasNetChanges } = useSelection();
  const [showExitWarning, setShowExitWarning] = useState(false);

  const [applyProposedChangesSelection, { loading: isApplying }] =
    useApplyProposedChangesSelectionMutation({
      onError: (e) => toast.error(e.message),
      refetchQueries: ["GetProfilesForCase", "GetComplianceCase", "IsProfileDirty"],
      awaitRefetchQueries: true,
    });

  const { queryResults, rows, currentProfile, publishedProfile, proposedProfile } =
    useComparisonRows({
      sourceProfileId,
      targetProfileId,
      latestVersionId,
      caseId,
      programId,
      caseType,
      fatcaCrsEnabled,
    });

  const navigateToProfiles = useCallback(() => {
    history.push(`${caseBasePath}/profiles`);
  }, [caseBasePath, history]);

  const handleExit = useCallback(() => {
    if (readonly || !hasNetChanges()) {
      navigateToProfiles();
      return;
    }
    setShowExitWarning(true);
  }, [readonly, hasNetChanges, navigateToProfiles]);

  const handleConfirmExit = useCallback(() => {
    setShowExitWarning(false);
    navigateToProfiles();
  }, [navigateToProfiles]);

  const handleDocumentClick = useCallback(
    (docId: number, profileId: number) => {
      const params = new URLSearchParams({
        profileId: String(profileId),
        documentId: String(docId),
        returnTo: RETURN_TO_REVIEW,
      });
      if (latestVersionId != null) params.set("latestVersionId", String(latestVersionId));
      history.push(
        `${caseBasePath}/profiles/${sourceProfileId}/proposed-changes/${targetProfileId}/documents?${params.toString()}`,
      );
    },
    [caseBasePath, history, sourceProfileId, targetProfileId, latestVersionId],
  );

  const handleApply = useCallback(async () => {
    const input = buildMutationPayload({
      caseId,
      targetProfileId,
      programId,
      latestVersionId,
      rows,
      getSelection,
      currentProfile,
      publishedProfile,
      proposedProfile,
    });
    const result = await applyProposedChangesSelection({ variables: { input } });
    if (result.data?.applyProposedChangesSelection) {
      toast.success("Proposed changes applied");
      navigateToProfiles();
    }
  }, [
    caseId,
    targetProfileId,
    programId,
    latestVersionId,
    rows,
    getSelection,
    currentProfile,
    publishedProfile,
    proposedProfile,
    applyProposedChangesSelection,
    navigateToProfiles,
  ]);

  const investingProfileName = currentProfile ? getInvestingProfileName(currentProfile) : "";

  const hasPublished = publishedProfile != null;

  const applyEnabled = rows.length > 0;

  const columnDefs = useMemo(
    () =>
      getColumnDefs({
        onDocumentClick: handleDocumentClick,
        currentProfileId: sourceProfileId,
        publishedProfileId: latestVersionId,
        proposedProfileId: targetProfileId,
        getSelection,
        setSelection,
        hasPublished,
        readonly,
      }),
    [
      handleDocumentClick,
      sourceProfileId,
      latestVersionId,
      targetProfileId,
      getSelection,
      setSelection,
      hasPublished,
      readonly,
    ],
  );

  return (
    <FullscreenTakeover>
      <FullscreenTakeover.Header
        title="Proposed changes"
        subtitle={investingProfileName}
        onExit={handleExit}
      />
      <FullscreenTakeover.Body>
        <Flex flexDirection="column" alignItems="center" className={styles.fullWidth}>
          <QueryLoader result={queryResults}>
            {rows.length === 0 ? (
              <Text variant="regular-md" className={styles.emptyState}>
                No differences to review.
              </Text>
            ) : (
              <Card title="Proposed changes" className={styles.reviewCard}>
                <Flex flexDirection="column" gap={Sizes.MD}>
                  <Text variant="regular-sm" className={styles.helpText}>
                    Review each field below and select the value to publish. Your selections
                    will be saved as the latest published version of this profile, so review
                    carefully.
                  </Text>
                  <Text variant="regular-sm" className={styles.helpText}>
                    Note: Removing required data may cause this profile to lose its compliance
                    status.
                  </Text>
                  <div className={styles.gridContainer}>
                    <Grid<ComparisonRow>
                      reactiveCustomComponents
                      rowData={rows}
                      columnDefs={columnDefs}
                      gridOptions={gridOptions}
                    />
                  </div>
                </Flex>
              </Card>
            )}
          </QueryLoader>
        </Flex>
      </FullscreenTakeover.Body>
      <FullscreenTakeover.Footer>
        <Flex justifyContent="center" className={styles.fullWidth}>
          <Flex
            alignItems="center"
            gap={Sizes.MD}
            justifyContent="flex-end"
            className={styles.reviewCard}
          >
            <Button variant="neutral" onClick={handleExit}>
              {readonly ? "Close" : "Cancel"}
            </Button>
            {!readonly && (
              <Button
                variant="primary"
                onClick={handleApply}
                disabled={!applyEnabled || isApplying}
                isLoading={isApplying}
              >
                Apply selection
              </Button>
            )}
          </Flex>
        </Flex>
      </FullscreenTakeover.Footer>
      <ExitWarningModal
        show={showExitWarning}
        onCancel={() => setShowExitWarning(false)}
        onConfirm={handleConfirmExit}
      />
    </FullscreenTakeover>
  );
};
